import { supabase } from '../lib/supabase'

export interface NotificationMeta {
  id: string
  title: string
  body: string
  created_by: string
  created_at: string
}

export interface NotificationRecipientItem {
  id: string // notification_recipients.id
  notification_id: string
  user_id: string
  read_at: string | null
  created_at: string
  notification?: NotificationMeta
}

export const notificationsService = {
  async sendToGroups(params: { title: string; body: string; userGroupIds?: string[]; userIds?: string[] }) {
    const { title, body, userGroupIds, userIds } = params

    // Caso de envio apenas para usuários específicos (sem grupos): criar manualmente e inserir apenas os selecionados
    if ((!userGroupIds || userGroupIds.length === 0) && Array.isArray(userIds) && userIds.length > 0) {
      try {
        const { data: authInfo } = await supabase.auth.getUser()
        const currentUserId = (authInfo as any)?.user?.id
        if (!currentUserId) {
          return { data: null as any, error: new Error('Usuário não autenticado') }
        }

        const { data: notif, error: insErr } = await supabase
          .from('notifications')
          .insert({ title, body, created_by: currentUserId })
          .select('id')
          .single()

        if (insErr || !notif) {
          return { data: null as any, error: insErr }
        }

        await supabase
          .from('notification_recipients')
          .insert(userIds.map(uid => ({ notification_id: notif.id, user_id: uid })))

        return { data: notif.id as any, error: null as any }
      } catch (e: any) {
        return { data: null as any, error: e }
      }
    }
    // Tenta nova assinatura do RPC (com p_user_ids)
    let { data, error } = await supabase.rpc('create_notification', {
      p_title: title,
      p_body: body,
      p_user_group_ids: userGroupIds || null,
      p_user_ids: userIds || null,
    })

    if (error && /create_notification/.test(error.message || '')) {
      // Fallback para versão antiga (sem p_user_ids) quando há grupos
      if (userGroupIds && userGroupIds.length > 0) {
        // Há grupos selecionados: usar função antiga e depois completar usuários específicos
        const fallback = await supabase.rpc('create_notification', {
          p_title: title,
          p_body: body,
          p_user_group_ids: userGroupIds,
        })
        data = fallback.data as any
        error = fallback.error as any

        // Se há usuários específicos e obtivemos o id da notificação, inserir manualmente evitando duplicar usuários que já vieram por grupo
        if (!error && data && Array.isArray(userIds) && userIds.length > 0) {
          try {
            let userIdsToInsert = userIds
            const { data: overlapped, error: overlapErr } = await supabase
              .from('profiles')
              .select('id, user_group_id')
              .in('id', userIds)
            if (!overlapErr && Array.isArray(userGroupIds) && userGroupIds.length > 0) {
              const overlappedSet = new Set((overlapped || []).filter((r: any) => userGroupIds.includes(r.user_group_id)).map((r: any) => r.id))
              userIdsToInsert = userIds.filter(uid => !overlappedSet.has(uid))
            }

            if (userIdsToInsert.length > 0) {
              await supabase
                .from('notification_recipients')
                .insert(userIdsToInsert.map(uid => ({ notification_id: data as string, user_id: uid })))
            }
          } catch (e) {
            // Ignorar erro de inserção complementar
          }
        }
      }
    }

    return { data, error }
  },

  async fetchUnreadCount(userId?: string) {
    let query = supabase
      .from('notification_recipients')
      .select('*', { count: 'exact', head: true })
      .is('read_at', null)
      .is('deleted_at', null)

    if (userId) {
      query = query.eq('user_id', userId)
    }

    const { count, error } = await query
    return { count: count || 0, error }
  },

  async fetchLatest(limit: number = 15, userId?: string) {
    let query = supabase
      .from('notification_recipients')
      .select('id, notification_id, user_id, read_at, created_at, notification:notification_id(id, title, body, created_by, created_at)')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (userId) {
      query = query.eq('user_id', userId)
    }

    const { data, error } = await query

    const items = (data || []).map((row: any) => ({
      ...row,
      notification: Array.isArray(row.notification) ? row.notification[0] : row.notification
    })) as NotificationRecipientItem[]

    return { data: items, error }
  },

  async markAsRead(recipientId: string) {
    const { data, error } = await supabase
      .from('notification_recipients')
      .update({ read_at: new Date().toISOString() })
      .eq('id', recipientId)
      .select()
      .single()

    return { data, error }
  },

  async deleteRecipient(recipientId: string) {
    const { error } = await supabase
      .from('notification_recipients')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', recipientId)

    return { error }
  },

  async clearAllForUser(userId: string) {
    const { error } = await supabase
      .from('notification_recipients')
      .update({ deleted_at: new Date().toISOString() })
      .eq('user_id', userId)
      .is('deleted_at', null)

    return { error }
  },

  subscribeRealtime(userId: string, onChange: () => void) {
    // Notar: Supabase Realtime v2 usa filtro via 'filter'
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on('postgres_changes' as any, {
        event: 'INSERT',
        schema: 'public',
        table: 'notification_recipients',
        filter: `user_id=eq.${userId}`
      }, onChange)
      .on('postgres_changes' as any, {
        event: 'UPDATE',
        schema: 'public',
        table: 'notification_recipients',
        filter: `user_id=eq.${userId}`
      }, onChange)
      .on('postgres_changes' as any, {
        event: 'DELETE',
        schema: 'public',
        table: 'notification_recipients',
        filter: `user_id=eq.${userId}`
      }, onChange)
      .subscribe()

    return () => {
      try { supabase.removeChannel(channel) } catch {}
    }
  }
}


